
import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

interface PasswordResetFormProps {
  email: string;
  setEmail: (email: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  onBackToLogin: () => void;
}

export function PasswordResetForm({
  email,
  setEmail,
  isLoading,
  setIsLoading,
  onBackToLogin
}: PasswordResetFormProps) {
  
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success("Password reset link sent!", {
        description: `We've sent a password reset link to ${email}. Check your inbox to continue.`,
      });
      
      onBackToLogin();
    } catch (error: any) {
      console.error("Error sending password reset link:", error);
      toast.error("Failed to send password reset link", {
        description: error.message || "An error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handlePasswordReset} className="space-y-4">
      <div className="p-4 border border-amber-200 bg-amber-50 rounded-md mb-4">
        <p className="text-sm text-amber-800">
          Enter your email address below. We'll send you a link to reset your password. 
          Click the link in the email, and you'll be able to create a new password.
        </p>
      </div>
      
      <div className="space-y-2">
        <label 
          htmlFor="email-reset" 
          className="block text-sm font-medium"
        >
          Email
        </label>
        <div className="relative">
          <Input
            id="email-reset"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="w-full ps-10"
            required
          />
          <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      
      <Button 
        type="submit" 
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Sending link..." : "Send Password Reset Link"}
      </Button>
      
      <div className="mt-4 text-center">
        <Button 
          type="button"
          variant="link"
          onClick={onBackToLogin}
          className="text-sm"
        >
          Back to login
        </Button>
      </div>
    </form>
  );
}
